import type { BadgeProps } from '../components/ui/badge';
import { humanizeKey } from './labels';

export type StatusVariant = NonNullable<BadgeProps['variant']>;

/**
 * The six tones a state or attribute can take. Every registry entry uses one;
 * `brand` is for attributes only (Public, New), never a runtime state.
 */
export type StatusTone = Extract<StatusVariant, 'success' | 'warning' | 'info' | 'destructive' | 'muted' | 'brand'>;

export interface StatusStyle {
  variant: StatusTone;
  /** Runtime states show a dot; static attributes don't. */
  dot?: boolean;
  label?: string;
}

/**
 * The single registry mapping domain states to visuals. Pages pass the raw
 * status string to StatusBadge — never Tailwind classes. New states get one
 * line here.
 *
 * Rule: blue (brand) is reserved for attributes like Public; runtime state is
 * only ever success / info / warning / destructive / muted.
 *
 * ONE WORD PER STATE. Filter options, counts and field labels about a state
 * read the label here through `statusLabel` rather than retyping it, and a
 * StatusBadge `label` override may add context ("Live v1.4", "3 in review")
 * but never substitute another word or borrow another state's key.
 */
export const STATUS_REGISTRY: Record<string, StatusStyle> = {
  // Generic lifecycle
  active: { variant: 'success', dot: true, label: 'Active' },
  inactive: { variant: 'muted', dot: true, label: 'Inactive' },
  enabled: { variant: 'success', dot: true, label: 'Enabled' },
  disabled: { variant: 'muted', dot: true, label: 'Disabled' },
  online: { variant: 'success', dot: true, label: 'Online' },
  offline: { variant: 'destructive', dot: true, label: 'Offline' },
  // Processor runtime
  running: { variant: 'success', dot: true, label: 'Running' },
  starting: { variant: 'info', dot: true, label: 'Starting' },
  pending: { variant: 'info', dot: true, label: 'Pending' },
  stopping: { variant: 'warning', dot: true, label: 'Stopping' },
  // processor_status values the orchestrator writes on the way down or across.
  // Warning, like stopping: a transition, not an end state.
  draining: { variant: 'warning', dot: true, label: 'Draining' },
  terminating: { variant: 'warning', dot: true, label: 'Terminating' },
  failover: { variant: 'warning', dot: true, label: 'Failover' },
  // subscription_status: a paused or deleting subscription delivers nothing.
  paused: { variant: 'muted', dot: true, label: 'Paused' },
  deleting: { variant: 'muted', dot: true, label: 'Deleting' },
  stopped: { variant: 'muted', dot: true, label: 'Stopped' },
  terminated: { variant: 'muted', dot: true, label: 'Terminated' },
  degraded: { variant: 'warning', dot: true, label: 'Degraded' },
  failed: { variant: 'destructive', dot: true, label: 'Failed' },
  error: { variant: 'destructive', dot: true, label: 'Error' },
  // Sensors
  decommissioned: { variant: 'muted', dot: true, label: 'Decommissioned' },
  // License keys — "expired" is a lapsed date or a spent budget, with the reason
  // beside the badge; a manual revocation reads "revoked" below.
  expired: { variant: 'destructive', dot: true, label: 'Expired' },
  // Membership / invitations
  invited: { variant: 'info', dot: true, label: 'Invited' },
  suspended: { variant: 'warning', dot: true, label: 'Suspended' },
  // Template publishing lifecycle
  draft: { variant: 'warning', dot: true, label: 'Draft' },
  published: { variant: 'success', dot: true, label: 'Published' },
  superseded: { variant: 'muted', dot: true, label: 'Superseded' },
  // Template version review (API migration 0013). `pending` above is the
  // processor runtime state and reads "Pending"; a version awaiting a decision
  // needs its own wording, so it gets its own key rather than borrowing that
  // one. Rejected is destructive (it never ran); revoked is warning (it ran and
  // was withdrawn) — different facts, deliberately different colours.
  pending_review: { variant: 'info', dot: true, label: 'In review' },
  approved: { variant: 'success', dot: true, label: 'Approved' },
  rejected: { variant: 'destructive', dot: true, label: 'Rejected' },
  revoked: { variant: 'warning', dot: true, label: 'Revoked' },
  /**
   * The live version — `isActive`, which is what the orchestrator runs. Not
   * "the selected version": a selected version that was revoked is Revoked.
   */
  live: { variant: 'success', dot: true, label: 'Live' },
  /** The live version is behind the latest pushed one. */
  update_available: { variant: 'warning', dot: true, label: 'Update available' },
  // Row marks after a write (useInfiniteList prependItem / patchItem). Facts
  // about this session's edits, not runtime state: no dot, and neither borrows
  // a runtime colour — they clear on the next reset.
  new: { variant: 'brand', label: 'New' },
  updated: { variant: 'muted', label: 'Updated' },
  // Attributes (no dot — not runtime states)
  public: { variant: 'brand', label: 'Public' },
  private: { variant: 'muted', label: 'Private' },
  /** System-administrator scope only (the system organization). Never a built-in role. */
  system: { variant: 'warning', label: 'System' },
  /** Role type. Both muted: a built-in role is not elevated privilege. */
  built_in: { variant: 'muted', label: 'Built-in' },
  custom: { variant: 'muted', label: 'Custom' },
  /**
   * A template's publisher credential. Configuration, not runtime state, so no
   * dot; missing is warning because since migration 0008 it is an anomaly.
   */
  credentials_provisioned: { variant: 'muted', label: 'Provisioned' },
  credentials_missing: { variant: 'warning', label: 'Missing' },
  /** App-client kind. Amber for the publisher: it is an elevated credential. */
  app: { variant: 'muted', label: 'App client' },
  template_publisher: { variant: 'warning', label: 'Template publisher' },
};

/**
 * The registry's word for a state, or the humanized key when it has none
 * ('some_state' → 'Some state'). A fallback, not a licence: register every
 * value the API can return.
 */
export function statusLabel(status: string): string {
  return STATUS_REGISTRY[status.trim().toLowerCase()]?.label ?? humanizeKey(status.trim());
}

/**
 * The registry tone for a status — the one source of state colour for every
 * surface that is not a badge (row stripes, map pins, legends). An
 * unregistered key is muted, exactly as StatusBadge renders it.
 */
export function statusTone(status: string): StatusTone {
  return STATUS_REGISTRY[status.trim().toLowerCase()]?.variant ?? 'muted';
}

/** Solid fill per tone: the badge dot, row stripes, legend swatches. */
export const STATUS_TONE_FILL: Record<StatusTone, string> = {
  success: 'bg-success-emphasis',
  warning: 'bg-warning-emphasis',
  info: 'bg-info-emphasis',
  destructive: 'bg-destructive-emphasis',
  muted: 'bg-muted-foreground',
  brand: 'bg-primary',
};

/** CSS variable per tone, for Leaflet colours read through `getTokenColor`. */
export const STATUS_TONE_TOKEN: Record<StatusTone, string> = {
  success: '--success-emphasis',
  warning: '--warning-emphasis',
  info: '--info-emphasis',
  destructive: '--destructive-emphasis',
  muted: '--muted-foreground',
  brand: '--primary',
};
